import type {
  UserDetailsModel,
  UserRefModel,
} from "@/app/lib/models/UserDetailsModel";

export const ACCESS_SUSPENDED_CODE = "ACCESS_SUSPENDED";
export const ACCESS_SUSPENDED_MESSAGE =
  "Your account access has been suspended. Please contact your guardian.";
export const ACCESS_SUSPENDED_NOTIFICATION_TYPE = "access_suspended";

export type AccessSuspensionSource =
  | "dashboard"
  | "trainee_list"
  | "session"
  | "api";

export function getAccessSuspendedGuardianMessage(firstName?: string): string {
  const subject = firstName?.trim()
    ? `${firstName.trim()}'s`
    : "This trainee's";
  return `${subject} access is suspended. They have been signed out and cannot log in until you restore access.`;
}

export function getRestoreAccessHintMessage(firstName?: string): string {
  const subject = firstName?.trim() || "this trainee";
  return `Click RESTORE ACCESS to let ${subject} log in again right away.`;
}

export function getSuspendedTraineesLoginToastMessage(
  suspended: Pick<UserDetailsModel, "first_name" | "email_id">[],
): string | null {
  if (suspended.length === 0) return null;
  const action = "Use RESTORE ACCESS on your dashboard to give access back.";
  const names = suspended.map((t) => t.first_name?.trim() || t.email_id);
  if (names.length === 1) {
    return `${names[0]}'s access is suspended. ${action}`;
  }
  const list = `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Access is suspended for ${list}. ${action}`;
}

export function getSuspendedLoginToastStorageKey(
  emailId: string,
  loginSessionId?: string | null,
): string {
  return `access_suspended_login_toast:${emailId}:${loginSessionId || "no-session"}`;
}

export function isTraineeSuspended(
  user?: Pick<UserDetailsModel, "access_suspended"> | null,
): boolean {
  return user?.access_suspended === true;
}

export function getSuspendedTrainees(
  map: Map<string, UserDetailsModel>,
): UserDetailsModel[] {
  return Array.from(map.values()).filter((trainee) =>
    isTraineeSuspended(trainee),
  );
}

export function withSuspensionState(
  map: Map<string, UserDetailsModel>,
  email: string,
  suspended: boolean,
  sessionId?: string | null,
  meta?: {
    at?: unknown;
    by?: UserDetailsModel["access_suspended_by"];
  },
): Map<string, UserDetailsModel> {
  const current = map.get(email);
  if (!current) return map;
  const next = new Map(map);
  next.set(email, {
    ...current,
    access_suspended: suspended,
    access_suspended_session_id: suspended
      ? (sessionId ?? undefined)
      : undefined,
    ...(suspended
      ? {
          ...(meta?.at !== undefined ? { access_suspended_at: meta.at } : {}),
          ...(meta?.by ? { access_suspended_by: meta.by } : {}),
        }
      : { access_suspended_at: undefined, access_suspended_by: undefined }),
  });
  return next;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  center_head: "Center Head",
  guardian: "Guardian",
  trainee: "Trainee",
};

export function shouldConfirmRestore(viewerRole?: string | null): boolean {
  return viewerRole === "admin" || viewerRole === "center_head";
}

function toSuspendedDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    return new Date(value);
  }
  if (value && typeof value === "object") {
    const ts = value as { seconds?: number; _seconds?: number };
    const seconds = ts.seconds ?? ts._seconds;
    if (typeof seconds === "number") return new Date(seconds * 1000);
  }
  return null;
}

export function formatSuspendedAt(value: unknown): string | null {
  const date = toSuspendedDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fullName(
  user?: Pick<UserDetailsModel, "first_name" | "last_name"> | null,
): string | null {
  const name = [user?.first_name, user?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

export type RestoreAccessDetails = {
  trainee: { name: string | null; email: string; phone: string | null };
  guardians: {
    name: string | null;
    relation: string | null;
    email: string;
    phone: string | null;
  }[];
  suspendedAt: string | null;
  suspendedBy: string | null;
};

export function buildRestoreAccessDetails(
  trainee: UserDetailsModel,
  guardians: Map<string, UserDetailsModel> | undefined,
): RestoreAccessDetails {
  // Typed as a 1-tuple on the model; narrowing that with Array.isArray can yield `never`.
  const rawGuardianRefs: unknown = trainee.guardian_refs;
  const guardianRefs: UserRefModel[] = Array.isArray(rawGuardianRefs)
    ? (rawGuardianRefs as UserRefModel[])
    : [];
  const by = trainee.access_suspended_by;
  let suspendedBy: string | null = null;
  if (by?.email_id) {
    const who =
      fullName(guardians?.get(by.email_id)) ||
      (by.email_id === trainee.email_id ? fullName(trainee) : null) ||
      by.email_id;
    const role = ROLE_LABELS[by.role] ?? by.role;
    suspendedBy = role ? `${who} (${role})` : who;
  }
  return {
    trainee: {
      name: fullName(trainee),
      email: trainee.email_id,
      phone: trainee.phone_number || null,
    },
    guardians: guardianRefs
      .filter((ref) => !!ref?.email_id)
      .map((ref) => {
        const guardian = guardians?.get(ref.email_id);
        return {
          name: fullName(guardian),
          relation: ref.relation || null,
          email: ref.email_id,
          phone: guardian?.phone_number || null,
        };
      }),
    suspendedAt: formatSuspendedAt(trainee.access_suspended_at),
    suspendedBy,
  };
}

export function isAccessSuspendedResponse(
  resp?: { status?: number; data?: any } | null,
): boolean {
  return resp?.status === 403 && resp?.data?.code === ACCESS_SUSPENDED_CODE;
}

export function shouldForceLogoutForNotification(
  type: string,
  role?: string | null,
): boolean {
  return type === ACCESS_SUSPENDED_NOTIFICATION_TYPE && role === "trainee";
}
